import type { LabsApi } from '@gen/wklr-backend-api/v1/api';
import type {
  PostLabsWandhEvaluationParamsBody,
  PostLabsWandhQuestionParamsBodyDocumentTypeEnum,
  GetQuestionHistoryDetail200Response,
  HistoryDetailResponse,
} from '@gen/wklr-backend-api/v1/model';
import { LabsWandhModelSelectionEnum } from '@gen/wklr-backend-api/v1/model';
import { isAxiosError } from '../utils/axiosUtis';
import type { components, paths } from '@gen/wandh';
import type { AxiosError, AxiosInstance } from 'axios';
import { RateLimitError, QuotaExceededError } from '../errors';

export type WandhMessage = components['schemas']['Message'];
export type PostDeepResearchPlanParams = components['schemas']['PostDeepResearchPlanParams'];
export type PutDeepResearchPlanParams = components['schemas']['PutDeepResearchPlanParams'];
export type DeepResearchPlanResponse = components['schemas']['DeepResearchPlanResponse'];
export type DeepResearchReport = components['schemas']['ResearchReport'];
export type QuestionStreamResponse = paths['/question/']['post']['responses'][200]['content']['application/json'];

export type DeepResearchStreamResponse =
  | components['schemas']['DeepResearchResponse']
  | components['schemas']['ThinkingResponse']
  | components['schemas']['WatsonHighlightResponse']
  | components['schemas']['SystemResponse'];

// Deep Research Job 関連の型定義
export interface JobResponse {
  jobId: string;
  jobType: string;
  questionId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  executionId: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EventsResponse {
  events: Array<{
    id: string;
    eventType: string;
    payload: Record<string, unknown>;
    createdAt: string;
  }>;
  cursor: string | null;
}

export type LabsWandhHistoryApiResponse = QuestionStreamResponse | DeepResearchStreamResponse;

export interface LabsWandhQuestionHistoryListItem {
  id: string;
  questionSummary: string | null;
  selection: LabsWandhModelSelectionEnum;
  askedAt: string;
}

type NonNullableProperties<T> = {
  [P in keyof T]: NonNullable<T[P]>;
};

export type NonNullableLabsWandhQuestionHistoryListItem = NonNullableProperties<LabsWandhQuestionHistoryListItem>;

export function isNonNullableLabsWandhQuestionHistoryListItem(
  item: LabsWandhQuestionHistoryListItem,
): item is NonNullableLabsWandhQuestionHistoryListItem {
  return item.questionSummary !== null;
}

export interface LabsWandhQuestionHistory {
  id: string; // historyId (会話チェーンのID)
  questionId: string; // 個別質問のID
  parameters: QuestionParams;
  apiResponses: LabsWandhHistoryApiResponse[];
  evaluation: Evaluation | null;
  deepResearchPlan?: components['schemas']['ResearchPlan'] | null;
  deepResearchModifyHints?: components['schemas']['ResearchModifyHint'][] | null;
}

export interface LabsWandhStreamParams {
  query: PostLabsWandhQuestionParams;
  host: string;
  token: string | undefined;
  user: string;
  selection: LabsWandhModelSelectionEnum;
  reservedQuotaId: string;
  historyId: string;
  questionId: string;
  includesHolmes: boolean;
  signature: string;
  nonce: string;
  iat: number;
  parentId?: string;
  sessionHistory?: { type: 'user' | 'assistant'; text: string }[];
  planParams?: PostDeepResearchPlanParams;
}

function isEvaluation(value: unknown): value is Evaluation {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  if (!('holmes' in value)) {
    return false;
  }
  const { holmes } = value as { holmes: unknown };
  return typeof holmes === 'object' && holmes !== null;
}

export class LabsRepository {
  constructor(private api: LabsApi) {}

  async listLabsWandhQuestionHistories(
    perPage: number,
    after?: string,
  ): Promise<{ data: LabsWandhQuestionHistoryListItem[]; nextCursor: string | null }> {
    const response = await this.api.listQuestionHistories(perPage, after);
    const { data, nextCursor } = response.data as {
      data: LabsWandhQuestionHistoryListItem[];
      nextCursor: string | null;
    };
    return { data, nextCursor };
  }

  private normalizeHistoryDetailResponse(
    data: GetQuestionHistoryDetail200Response,
  ): LabsWandhQuestionHistory | LabsWandhQuestionHistory[] {
    const normalizeOne = (item: HistoryDetailResponse): LabsWandhQuestionHistory => {
      const {
        id: questionId,
        historyId,
        parameters,
        apiResponses,
        evaluation,
        deepResearchPlan: rawResearchPlan,
        deepResearchModifyHints: rawResearchModifyHints,
      } = item;

      if (typeof questionId !== 'string') {
        throw new Error('Invalid type for questionId');
      }

      if (typeof historyId !== 'string') {
        throw new Error('Invalid type for historyId');
      }

      if (
        parameters === null ||
        typeof parameters !== 'object' ||
        typeof (parameters as { question?: unknown }).question !== 'string' ||
        typeof (parameters as { askedAt?: unknown }).askedAt !== 'string'
      ) {
        throw new Error('Invalid types for parameters');
      }

      const castedParameters = parameters as unknown as QuestionParams;

      if (!Array.isArray(apiResponses)) {
        throw new Error('apiResponses must be an array');
      }

      for (const response of apiResponses) {
        if (
          response === null ||
          typeof response !== 'object' ||
          !('role' in response) ||
          typeof (response as { role: unknown }).role !== 'string'
        ) {
          throw new Error('Invalid type for an entry in apiResponses');
        }
      }

      const castedApiResponses =
        apiResponses as unknown as paths['/question/']['post']['responses'][200]['content']['application/json'][];

      let castedEvaluation: Evaluation | null = null;
      if (evaluation !== null && evaluation !== undefined) {
        if (!isEvaluation(evaluation)) {
          throw new Error('Invalid type for evaluation');
        }
        castedEvaluation = evaluation;
      }

      const castedDeepResearchPlan =
        (rawResearchPlan as unknown as components['schemas']['ResearchPlan'] | null | undefined) ?? null;
      const castedDeepResearchModifyHints =
        (rawResearchModifyHints as unknown as components['schemas']['ResearchModifyHint'][] | null | undefined) ?? null;

      return {
        id: historyId,
        questionId,
        parameters: castedParameters,
        apiResponses: castedApiResponses,
        evaluation: castedEvaluation,
        deepResearchPlan: castedDeepResearchPlan,
        deepResearchModifyHints: castedDeepResearchModifyHints,
      };
    };

    if (Array.isArray(data)) {
      return data.map((item) => normalizeOne(item));
    }

    return normalizeOne(data);
  }

  async getLabsWandhQuestionHistory(id: string): Promise<LabsWandhQuestionHistory | LabsWandhQuestionHistory[]> {
    const { data } = await this.api.getQuestionHistoryDetail(id);
    return this.normalizeHistoryDetailResponse(data);
  }

  async deleteLabsWandhQuestionHistory(id: string): Promise<void> {
    try {
      await this.api.deleteQuestionHistory(id);
    } catch (error) {
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 404:
              throw new Error('指定された履歴が見つかりません。');
            default:
              throw new Error(`履歴の削除中にエラーが発生しました。コード: ${axiosError.response.status}`);
          }
        } else if (axiosError.request) {
          throw new Error('削除リクエストが送信されましたが、応答がありませんでした。');
        } else {
          throw new Error('削除リクエストの設定中に問題が発生しました。');
        }
      } else {
        throw new Error('予期せぬ問題が発生しました。');
      }
    }
  }

  async createLabsWandhShare(historyId: string): Promise<string> {
    try {
      const { data } = await this.api.createHistoryShare(historyId);
      if (!data || typeof data.shareId !== 'string') {
        throw new Error('共有IDの取得に失敗しました。');
      }
      return data.shareId;
    } catch (error) {
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 404:
              throw new Error('指定された履歴が見つかりません。');
            default:
              throw new Error(`共有の作成中にエラーが発生しました。コード: ${axiosError.response.status}`);
          }
        } else if (axiosError.request) {
          throw new Error('リクエストが送信されましたが、応答がありませんでした。');
        } else {
          throw new Error('リクエストの設定中に問題が発生しました。');
        }
      } else {
        throw new Error('予期せぬ問題が発生しました。');
      }
    }
  }

  async getLabsWandhShare(historyId: string): Promise<string | null> {
    try {
      const { data } = await this.api.getHistoryShare(historyId);
      if (!data || typeof data.shareId !== 'string') {
        return null;
      }
      return data.shareId;
    } catch (error) {
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 404:
              // 共有がまだ作成されていない場合はnullを返す
              return null;
            default:
              throw new Error(`共有情報の取得中にエラーが発生しました。コード: ${axiosError.response.status}`);
          }
        } else if (axiosError.request) {
          throw new Error('リクエストが送信されましたが、応答がありませんでした。');
        } else {
          throw new Error('リクエストの設定中に問題が発生しました。');
        }
      } else {
        throw new Error('予期せぬ問題が発生しました。');
      }
    }
  }

  async deleteLabsWandhShare(historyId: string): Promise<void> {
    try {
      await this.api.deleteHistoryShare(historyId);
    } catch (error) {
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 404:
              throw new Error('指定された共有が見つかりません。');
            default:
              throw new Error(`共有の削除中にエラーが発生しました。コード: ${axiosError.response.status}`);
          }
        } else if (axiosError.request) {
          throw new Error('削除リクエストが送信されましたが、応答がありませんでした。');
        } else {
          throw new Error('削除リクエストの設定中に問題が発生しました。');
        }
      } else {
        throw new Error('予期せぬ問題が発生しました。');
      }
    }
  }

  async getLabsWandhQuestionHistoryByShareId(
    shareId: string,
  ): Promise<LabsWandhQuestionHistory | LabsWandhQuestionHistory[]> {
    try {
      const { data } = await this.api.getHistoryShareDetail(shareId);
      return this.normalizeHistoryDetailResponse(data);
    } catch (error) {
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 404:
              throw new Error('指定された共有が見つかりません。');
            default:
              throw new Error(`共有履歴の取得中にエラーが発生しました。コード: ${axiosError.response.status}`);
          }
        } else if (axiosError.request) {
          throw new Error('リクエストが送信されましたが、応答がありませんでした。');
        } else {
          throw new Error('リクエストの設定中に問題が発生しました。');
        }
      } else {
        throw new Error('予期せぬ問題が発生しました。');
      }
    }
  }

  async preparePostLabsWandhQuestion(
    query: PostLabsWandhQuestionParams,
    parentHistoryId?: string,
  ): Promise<LabsWandhStreamParams> {
    const documentType = query.filter?.documentType as PostLabsWandhQuestionParamsBodyDocumentTypeEnum[];
    if (!documentType) {
      throw new Error('documentTypeが指定されていません。');
    }
    // filter.id を filterIds としてバックエンドに送信（バリデーション用）
    const filterIds = query.filter?.id as string[] | undefined;

    try {
      const {
        data: {
          host,
          token,
          user,
          selection,
          reservedQuotaId,
          historyId,
          questionId,
          includesHolmes,
          validatedFilterIds,
          signature,
          nonce,
          iat,
        },
      } = await this.api.postLabsWandhQuestion({
        selection: query.selection,
        documentType,
        historyId: parentHistoryId,
        ...(filterIds && { filterIds }),
      });

      // バックエンドで検証済みのIDリストでqueryを上書き
      const validatedQuery =
        validatedFilterIds && query.filter ? { ...query, filter: { ...query.filter, id: validatedFilterIds } } : query;

      return {
        query: validatedQuery,
        host,
        token,
        user,
        selection,
        reservedQuotaId,
        historyId,
        questionId,
        includesHolmes,
        signature,
        nonce,
        iat,
      };
    } catch (error) {
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 429:
              if (axiosError.response.data.type === 'quota_exceeded') {
                throw new QuotaExceededError('残り回数が0回です。');
              }
              throw new RateLimitError('検索の間隔が短すぎます。少し待ってから再度お試しください。');
            case 403:
              throw new Error('判例連携オプションが有効化されていません。文献・情報から判例を除いてください。');
            default:
              throw new Error(`通信中に問題が発生しました。コード: ${axiosError.response.status}`);
          }
        } else if (axiosError.request) {
          throw new Error('検索リクエストが送信されましたが、応答がありませんでした。');
        } else {
          throw new Error('検索リクエストの設定中に問題が発生しました。');
        }
      } else {
        throw new Error('予期せぬ問題が発生しました。');
      }
    }
  }

  async getLabsWandhStream(params: LabsWandhStreamParams) {
    const {
      query,
      host,
      token,
      user,
      reservedQuotaId,
      historyId,
      questionId,
      includesHolmes,
      signature,
      nonce,
      iat,
      sessionHistory,
      parentId,
    } = params;
    const controller = new AbortController();
    const version = LabsWandhAvailableVersions[query.selection];

    return {
      controller,
      async stream() {
        const response = await fetch(host.replace(/\/+$/, '') + '/question/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            ...query,
            version,
            user,
            reservedQuotaId,
            historyId,
            questionId,
            includesHolmes,
            signature,
            nonce,
            iat,
            sessionHistory,
            parentId,
          }),
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw response;
        }

        return {
          async *[Symbol.asyncIterator]() {
            const reader = response.body!.getReader();
            const utf8Decoder = new TextDecoder();
            let queue = '';
            let readResult = await reader.read();
            while (!readResult.done) {
              queue += utf8Decoder.decode(readResult.value, { stream: true });

              if (queue.includes('\n')) {
                const lines = queue.split('\n');
                queue = lines.pop()!;
                yield* lines.map(
                  (line) =>
                    JSON.parse(line) as paths['/question/']['post']['responses'][200]['content']['application/json'],
                );
              }

              readResult = await reader.read();
            }
          },
        };
      },
    };
  }

  async getLabsWandhAgreement() {
    const { data } = await this.api.getLabsWandhAgreement();
    return data;
  }

  async postLabsWandhAgreement(ids: string[]) {
    const { data } = await this.api.postLabsWandhAgreement(ids);
    return data;
  }

  async postLabsWandhEvaluation(body: PostLabsWandhEvaluationParamsBody) {
    const { data } = await this.api.postLabsWandhEvaluation(body);
    return data;
  }

  async getLabsWandhQuota() {
    const { data } = await this.api.getLabsWandhQuota();
    return data;
  }

  async postDeepResearchPlan(args: {
    host: string;
    token: string | undefined;
    body: PostDeepResearchPlanParams;
  }): Promise<DeepResearchPlanResponse> {
    const { host, token, body } = args;
    const response = await fetch(host.replace(/\/+$/, '') + '/deep-research/plan', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw response;
    }

    return (await response.json()) as DeepResearchPlanResponse;
  }

  async putDeepResearchPlan(body: PutDeepResearchPlanParams): Promise<void> {
    const apiWithAxios = this.api as unknown as { axios: AxiosInstance; basePath?: string };
    const axiosInstance = apiWithAxios.axios;
    const basePath = apiWithAxios.basePath ?? '';

    await axiosInstance.put(basePath + '/wandh/agents/deep-research/plan', body);
  }

  // Deep Research Job を作成（wklr-backend-api 経由）
  async createDeepResearchJob(params: {
    questionId: string;
    deepResearchPlan: Record<string, unknown>;
    filter?: Record<string, unknown> | null;
  }): Promise<JobResponse> {
    const { questionId, deepResearchPlan, filter } = params;
    try {
      const { data } = await this.api.createDeepResearchJob({
        questionId,
        deepResearchPlan,
        filter: filter ?? null,
      });
      return {
        jobId: data.jobId,
        jobType: data.jobType,
        questionId: data.questionId,
        status: data.status,
        executionId: data.executionId ?? null,
        errorMessage: data.errorMessage ?? null,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      };
    } catch (error) {
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 400:
              throw new Error('Watson & Holmes の利用に関する同意が必要です。');
            case 402:
              throw new Error('プランに加入していません。');
            case 403:
              throw new Error('判例連携オプションが有効化されていません。文献・情報から判例を除いてください。');
            case 429: {
              const responseData = axiosError.response.data as { type?: string } | undefined;
              if (responseData?.type === 'quota_exceeded') {
                throw new QuotaExceededError('残り回数が0回です。');
              }
              throw new RateLimitError('検索の間隔が短すぎます。少し待ってから再度お試しください。');
            }
            default:
              throw new Error(`通信中に問題が発生しました。コード: ${axiosError.response.status}`);
          }
        } else if (axiosError.request) {
          throw new Error('Deep Research 実行リクエストが送信されましたが、応答がありませんでした。');
        } else {
          throw new Error('Deep Research 実行リクエストの設定中に問題が発生しました。');
        }
      }
      throw new Error('予期せぬ問題が発生しました。');
    }
  }

  // questionId から Job を取得（wklr-backend-api 経由）
  async getJobByQuestionId(params: { questionId: string }): Promise<JobResponse | null> {
    const { questionId } = params;
    try {
      const { data } = await this.api.getJobByQuestionId(questionId);
      return {
        jobId: data.jobId,
        jobType: data.jobType,
        questionId: data.questionId,
        status: data.status,
        executionId: data.executionId ?? null,
        errorMessage: data.errorMessage ?? null,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      };
    } catch (error) {
      if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 404) {
          return null;
        }
      }
      throw error;
    }
  }

  // Events を取得（ポーリング用、wklr-backend-api 経由）
  async getEvents(params: { questionId: string; after?: string }): Promise<EventsResponse> {
    const { questionId, after } = params;
    const { data } = await this.api.getEvents(questionId, after);
    return {
      events: data.events.map((event) => ({
        id: event.id,
        eventType: event.eventType,
        payload: event.payload,
        createdAt: event.createdAt,
      })),
      cursor: data.cursor ?? null,
    };
  }

  async getWandhAvailableBooks(params?: {
    keyword?: string;
    storeOffset?: number;
    storeLimit?: number;
    otherOffset?: number;
    otherLimit?: number;
  }) {
    const { data } = await this.api.getWandhAvailableBooks(
      params?.keyword,
      params?.storeOffset,
      params?.storeLimit,
      params?.otherOffset,
      params?.otherLimit,
    );
    return data;
  }
}

export { LabsWandhModelSelectionEnum };
export const LabsWandhAvailableVersions: Record<LabsWandhModelSelectionEnum, number> = {
  [LabsWandhModelSelectionEnum.Standard]: 518,
  [LabsWandhModelSelectionEnum.Advanced]: 517,
  [LabsWandhModelSelectionEnum.DeepResearch]: 1, // Deep Research
};
export type QuestionParams = components['schemas']['GetQuestionParams'];
export type PostLabsWandhQuestionParams = Omit<
  components['schemas']['PostQuestionParams'],
  'user' | 'historyId' | 'reservedQuotaId' | 'includesHolmes' | 'selection' | 'version'
> & {
  selection: LabsWandhModelSelectionEnum;
};
export type PostLabsWandhQuestionReference = components['schemas']['WatsonReference'];
export type CaseReferenceMetadata = components['schemas']['CaseReferenceMetadata'];
export type GuidelineReferenceMetadata = components['schemas']['GuidelineReferenceMetadata'];
export type Evaluation = Omit<PostLabsWandhEvaluationParamsBody, 'session'>;
